"use client";

import { LucideIcon } from "lucide-react";
import { ReactNode } from "react";

import { Button } from "@/src/components/shadcn/ui/button";
import { Card, CardTitle, CardHeader, CardContent } from "@/src/components/shadcn/ui/card";

export interface EditableCardProps<T> {
  title: string;
  icon: LucideIcon;
  initialData: T;
  onCancel: () => void;
  onSave: (updatedData: T) => Promise<void>;
  children: ReactNode;
  isSaving: boolean;
  createMode?: boolean;
}

export default function EditableCard<T>({
  title,
  icon: Icon,
  initialData,
  onCancel,
  onSave,
  children,
  isSaving,
  createMode = false,
}: EditableCardProps<T>) {
  const handleSave = async () => {
    await onSave(initialData);
  };

  const saveButtonText = createMode ? "Create" : "Update";
  const saveButtonLoadingText = createMode ? "Creating..." : "Updating...";

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Icon className="h-6 w-6 mr-2" />
            <CardTitle className="text-md font-semibold">{title}</CardTitle>
          </div>
          <div className="flex gap-2">
            <Button className="w-20" variant="outline" size="sm" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button className="w-24" size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? saveButtonLoadingText : saveButtonText}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
